import ToolModel from '../../../../../models/ToolModel';

/** Soft-active tools (exist); operational `active` is kept on each record. */
const EXISTING_TOOL_FILTERS = { is_active: true };

/** API page cap. The list endpoint defaults to 20, which drops tools past that. */
const TOOLS_PAGE_SIZE = 100;
const MAX_TOOL_PAGES = 20;

const cache = new Map();
const inFlight = new Map();

const toolsEnvelope = (response) => {
  if (!response || Array.isArray(response)) return response;

  const nested = response.data;
  const nestedIsEnvelope = nested
    && !Array.isArray(nested)
    && typeof nested === 'object'
    && (Array.isArray(nested.data) || Array.isArray(nested.tools) || Array.isArray(nested.results));

  return nestedIsEnvelope ? nested : response;
};

const normalizeToolsPayload = (response) => {
  if (!response) return [];

  const payload = toolsEnvelope(response);
  if (Array.isArray(payload)) return payload;

  return payload?.data || payload?.results || payload?.tools || [];
};

const readToolsPage = (response) => {
  const tools = normalizeToolsPayload(response);
  const envelope = toolsEnvelope(response);
  const total = Number(envelope?.total);

  return {
    tools,
    total: Number.isFinite(total) ? total : null
  };
};

const hasExplicitPlanFlag = (tool) => (
  typeof tool?.requires_subscription === 'boolean'
  || typeof tool?.plan_restricted === 'boolean'
);

/**
 * Plan lock comes from requires_subscription / plan_restricted.
 * Until the API sends that flag, active: false still locks the row.
 */
export const requiresSubscriptionFromTool = (tool) => {
  if (hasExplicitPlanFlag(tool)) {
    return tool.requires_subscription === true || tool.plan_restricted === true;
  }

  return tool?.active !== true;
};

export const extractAgentToolStates = (response) => (
  normalizeToolsPayload(response)
    .map((tool) => {
      const type = tool?.type;
      if (!type) return null;
      return {
        type,
        active: tool?.active === true,
        requiresSubscription: requiresSubscriptionFromTool(tool)
      };
    })
    .filter(Boolean)
);

/** Operationally active types only (active: true). Used by flow tool availability. */
export const extractActiveToolTypes = (response) => (
  extractAgentToolStates(response)
    .filter((tool) => tool.active)
    .map((tool) => tool.type)
);

const activeTypesFromStates = (states) => (
  (states || [])
    .filter((tool) => tool.active)
    .map((tool) => tool.type)
);

export const getCachedAgentToolStates = (agentId) => (
  agentId && cache.has(agentId) ? cache.get(agentId) : undefined
);

const fetchAllExistingTools = async (agentId) => {
  const tools = [];

  for (let page = 0; page < MAX_TOOL_PAGES; page += 1) {
    const response = await ToolModel.get(agentId, {
      ...EXISTING_TOOL_FILTERS,
      limit: TOOLS_PAGE_SIZE,
      skip: tools.length
    });
    const { tools: batch, total } = readToolsPage(response);
    tools.push(...batch);

    const reachedEnd = batch.length === 0
      || batch.length < TOOLS_PAGE_SIZE
      || (total != null && tools.length >= total);

    if (reachedEnd) break;
  }

  return tools;
};

export const fetchAgentToolStates = (agentId) => {
  if (!agentId) return Promise.resolve(null);

  if (cache.has(agentId)) {
    return Promise.resolve(cache.get(agentId));
  }

  const pending = inFlight.get(agentId);
  if (pending) return pending;

  const request = fetchAllExistingTools(agentId)
    .then((tools) => {
      const states = extractAgentToolStates(tools);
      cache.set(agentId, states);
      inFlight.delete(agentId);
      return states;
    })
    .catch(() => {
      cache.set(agentId, []);
      inFlight.delete(agentId);
      return [];
    });

  inFlight.set(agentId, request);
  return request;
};

export const fetchActiveToolTypes = (agentId) => (
  fetchAgentToolStates(agentId).then((states) => (
    states == null ? null : activeTypesFromStates(states)
  ))
);

export const preloadActiveTools = (agentId) => {
  fetchAgentToolStates(agentId);
};

export const invalidateActiveToolsCache = (agentId) => {
  if (agentId) {
    cache.delete(agentId);
    inFlight.delete(agentId);
    return;
  }

  cache.clear();
  inFlight.clear();
};
